import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import React, { useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronRight, ChevronLeft } from "lucide-react";
interface Links {
  label: string;
  href: string;
  icon: React.JSX.Element | React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}
interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
}
const SidebarContext = createContext<SidebarContextProps | undefined>(undefined);
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};
export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;
  return (
    <SidebarContext.Provider
      value={{
        open,
        setOpen,
        animate,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};
export const SidebarBody = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <>
      <DesktopSidebar className={className}>{children}</DesktopSidebar>
    </>
  );
};
export const DesktopSidebar = ({ className, children }: { className?: string; children: React.ReactNode }) => {
  const { open, setOpen, animate } = useSidebar();
  return (
    <motion.div
      className={cn(
        "h-[calc(100vh-4rem)] fixed top-16 left-0 py-4 hidden md:flex md:flex-col glass border-r flex-shrink-0 z-40 min-w-0",
        open ? "px-4" : "px-2",
        className,
      )}
      initial={false}
      animate={{
        width: animate ? (open ? "300px" : "72px") : "300px",
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          color: "hsl(var(--gold))",
        }}
        aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
        className="p-2 rounded-lg hover:bg-muted/50 transition-colors self-end px-[7px] py-[3px]"
      >
        {open ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
      </button>
      {children}
    </motion.div>
  );
};
export const MobileSidebar = ({ className, children }: { className?: string; children: React.ReactNode }) => {
  const { open, setOpen } = useSidebar();
  return (
    <>
      <div
        className={cn(
          "h-10 px-4 py-4 flex flex-row md:hidden items-center justify-between glass-premium border-b border-premium w-full",
        )}
      >
        <div className="flex justify-end z-20 w-full">
          <Menu
            className="cursor-pointer"
            style={{
              color: "hsl(var(--gold))",
            }}
            onClick={() => setOpen(!open)}
          />
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{
              x: "-100%",
              opacity: 0,
            }}
            animate={{
              x: 0,
              opacity: 1,
            }}
            exit={{
              x: "-100%",
              opacity: 0,
            }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
            className={cn(
              "fixed h-full w-full inset-0 glass-premium p-10 z-[100] flex flex-col justify-between md:hidden",
              className,
            )}
          >
            <div
              className="absolute right-10 top-10 z-50 cursor-pointer"
              style={{
                color: "hsl(var(--gold))",
              }}
              onClick={() => setOpen(!open)}
            >
              <X />
            </div>
            <div className="flex flex-col flex-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
export const SidebarLink = ({ link, className, ...props }: { link: Links; className?: string }) => {
  const { open, animate } = useSidebar();
  const location = useLocation();
  const isActive = location.pathname === link.href;
  const handleClick = (e: React.MouseEvent) => {
    if (link.onClick) {
      link.onClick(e);
    }
  };
  return (
    <Link
      to={link.href}
      onClick={handleClick}
      className={cn(
        "flex items-center group/sidebar py-2 rounded-lg transition-all duration-300 relative",
        open ? "justify-start gap-2 px-3" : "justify-center px-1",
        isActive ? "bg-gradient-to-r from-gold/20 to-scarlet-red/10 border-l-2" : "hover:bg-muted/50",
        className,
      )}
      style={
        isActive
          ? {
              borderLeftColor: "hsl(var(--gold))",
            }
          : undefined
      }
      {...props}
    >
      <span
        className={cn("flex-shrink-0 transition-transform duration-300", !open && "mx-auto")}
        style={
          isActive
            ? {
                color: "hsl(var(--gold))",
              }
            : undefined
        }
      >
        {link.icon}
      </span>
      <motion.span
        initial={false}
        animate={{
          width: animate ? (open ? "auto" : 0) : "auto",
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
        className={cn(
          "text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-nowrap overflow-hidden !p-0 !m-0",
          isActive ? "font-semibold" : "",
        )}
        style={{
          color: isActive ? "hsl(var(--gold))" : "hsl(var(--foreground))",
        }}
      >
        {link.label}
      </motion.span>
    </Link>
  );
};
